import { Directive, Input, OnChanges, OnInit, EventEmitter } from '@angular/core';
import { NG_VALIDATORS, Validator, ValidationErrors, FormControl } from '@angular/forms';
import { ValidationTypes } from './validator-const.model';
import { BaseValidator } from './base.directive';
import { isNumber, isDecimal, hasLeadingZeros } from '../../common/functions/js-linq';
import { TranslationService } from '../../common/services/translation.service';
import { CpqNumberOptions } from './cpq-number-options.model';

@Directive({
    selector: `[cpqNumber]`,
    providers: [{ provide: NG_VALIDATORS, useExisting: NumberDirective, multi: true }],
    standalone: false
})
export class NumberDirective extends BaseValidator implements Validator, OnChanges, OnInit {

    private CONTAINS_WHITE_SPACE_REGEX = /\s/;
    
    constructor(private translationService: TranslationService) {
        super();
    }

    @Input('cpqNumberOptions') cpqNumberOptions: CpqNumberOptions= new CpqNumberOptions();
    private onChange: () => void;

    ngOnInit() {
        if (!this.cpqNumberOptions.onPropertyChange) {
            this.cpqNumberOptions.onPropertyChange = new EventEmitter<{ propertyName: string, propertyValue: any }>();
        }
        this.cpqNumberOptions.onPropertyChange.subscribe((propertyInfo : { propertyName: string, propertyValue: any }) => {
            if (this.onChange) {
                this.onChange();
            }
        });
    }

    ngOnChanges(): void {
        if (this.cpqNumberOptions) {
            this.cpqNumberOptions.allowDecimal = this.cpqNumberOptions.allowDecimal !== undefined ? this.cpqNumberOptions.allowDecimal : true;
            this.cpqNumberOptions.includeMaxValue = this.cpqNumberOptions.includeMaxValue !== undefined ? this.cpqNumberOptions.includeMaxValue : true;
            this.cpqNumberOptions.includeMinValue = this.cpqNumberOptions.includeMinValue !== undefined ? this.cpqNumberOptions.includeMinValue : true;
            this.cpqNumberOptions.minValue = this.cpqNumberOptions.minValue  !== undefined ? this.cpqNumberOptions.minValue : 0;
            this.cpqNumberOptions.maxValue = this.cpqNumberOptions.maxValue !== undefined ? this.cpqNumberOptions.maxValue : Number.MAX_SAFE_INTEGER;           
        }
        
        if (this.onChange) {
            this.onChange();
        }
    }

    validate(c: FormControl<string>): ValidationErrors {
        if (c.value) {
            if (!isNumber(c.value)) {
                return this.createMessage(ValidationTypes.cpqNumber, this.translationService.translate('Admin.Validation.NotNumber'));
            }
            if (hasLeadingZeros(+c.value)) {
                return this.createMessage(ValidationTypes.cpqNumber, this.translationService.translate('Admin.Validation.CannotStartWithZero'));
            }            
            if(this.CONTAINS_WHITE_SPACE_REGEX.test(c.value)) {
                return this.createMessage(ValidationTypes.cpqNumber, this.translationService.translate('Admin.Validation.ContainsWhiteSpace'));
            }

            if(this.cpqNumberOptions){
                const numberValue = +c.value;
                if (!this.cpqNumberOptions.allowDecimal && isDecimal(c.value)) {
                    return this.createMessage(ValidationTypes.cpqNumber, this.translationService.translate('Admin.Validation.MustNotBeDecimal'));
                }
    
                if (this.cpqNumberOptions.includeMinValue && numberValue < this.cpqNumberOptions.minValue) {
                    return this.createMessage(ValidationTypes.cpqNumber, `${this.translationService.translate('Admin.Validation.MustBeGreaterOrEqual')} ${this.cpqNumberOptions.minValue}`);
                }
                if (!this.cpqNumberOptions.includeMinValue && numberValue <= this.cpqNumberOptions.minValue) {
                    return this.createMessage(ValidationTypes.cpqNumber, `${this.translationService.translate('Admin.Validation.MustBeGreaterThen')} ${this.cpqNumberOptions.minValue}`);
                }
                if (this.cpqNumberOptions.includeMaxValue && numberValue > this.cpqNumberOptions.maxValue) {
                    return this.createMessage(ValidationTypes.cpqNumber, `${this.translationService.translate('Admin.Validation.MustBeLessOrEqual')} ${this.cpqNumberOptions.maxValue}`);
                }
                if (!this.cpqNumberOptions.includeMaxValue && numberValue >= this.cpqNumberOptions.maxValue) {
                    return this.createMessage(ValidationTypes.cpqNumber, `${this.translationService.translate('Admin.Validation.MustBeLessThen')} ${this.cpqNumberOptions.maxValue}`);
                }
            }

        }

        return null;
    }

    registerOnValidatorChange(fn: () => void): void { this.onChange = fn };

}
